import { useState, useEffect, useLayoutEffect, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  ShoppingCart, Heart, Search, Menu, X, ChevronDown, ChevronRight,
  User, LogOut, Package, MapPin, FileText, Settings, HelpCircle, Store, Sparkles, PhoneCall, ArrowRight
} from 'lucide-react'
import { useAuthStore } from '../../store/auth.store'
import { useWishlistStore } from '../../store/wishlist.store'
import { useQuery } from '@tanstack/react-query'
import { getCart } from '../../services/cart.service'
import Topbar from './Topbar'
import SmartLink from '../common/SmartLink'
import { useSiteContent } from '../../content/useSiteContent'

// Shared look for desktop right-side action icons
const ACTION_ICON = 'h-5 w-5'
const ACTION_STROKE = 1.75

export default function Navbar() {
  const { site } = useSiteContent()
  const NAV_LINKS = site.header.nav.map((n) => ({
    label: n.label,
    href: n.href,
    dropdown: n.children?.length ? n.children : undefined,
  }))
  const TRENDING_SEARCHES = site.header.trending_searches || []
  const [scrolled, setScrolled] = useState(false)
  const [topbarShift, setTopbarShift] = useState(0)
  const topbarRef = useRef(null)
  const mainRowRef = useRef(null)
  const menuRowRef = useRef(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [openCategory, setOpenCategory] = useState(null)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const userMenuRef = useRef(null)
  const userMenuTimeoutRef = useRef(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearchFocused, setIsSearchFocused] = useState(false)

  const { isAuthenticated, user, logout } = useAuthStore()
  const { data: cart } = useQuery({ queryKey: ['cart'], queryFn: getCart })
  const cartCount = cart?.item_count || 0
  const { items: wishlistItems } = useWishlistStore()
  const navigate = useNavigate()
  const location = useLocation()

  // Lock body scroll when mobile menu drawer is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [mobileOpen])

  // Automatically close drawers on route change
  useEffect(() => {
    setMobileOpen(false)
    setUserMenuOpen(false)
  }, [location.pathname])

  // Publish header height CSS variable for perfect page offset
  useLayoutEffect(() => {
    const root = document.documentElement
    const els = [topbarRef.current, mainRowRef.current, menuRowRef.current].filter(Boolean)
    const publish = () => {
      const h = els.reduce((sum, el) => sum + el.offsetHeight, 0)
      root.style.setProperty('--header-h', h + 'px')
    }
    publish()
    const ro = new ResizeObserver(publish)
    els.forEach((el) => ro.observe(el))
    return () => {
      ro.disconnect()
      root.style.removeProperty('--header-h')
    }
  }, [])

  useEffect(() => {
    const handler = () => {
      setScrolled(window.scrollY > 20)
      const barHeight = topbarRef.current ? topbarRef.current.offsetHeight : 0
      setTopbarShift(Math.min(Math.max(window.scrollY, 0), barHeight))
    }
    handler()
    window.addEventListener('scroll', handler, { passive: true })
    return () => window.removeEventListener('scroll', handler)
  }, [])

  // Auto-close desktop user menu on outside click, Escape key, or scroll
  useEffect(() => {
    if (!userMenuOpen) return

    const handlePointerDown = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setUserMenuOpen(false)
      }
    }

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        setUserMenuOpen(false)
      }
    }

    const handleWindowScroll = () => {
      setUserMenuOpen(false)
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    window.addEventListener('scroll', handleWindowScroll, { passive: true })

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('scroll', handleWindowScroll)
    }
  }, [userMenuOpen])

  const handleUserMenuEnter = () => {
    if (userMenuTimeoutRef.current) {
      clearTimeout(userMenuTimeoutRef.current)
      userMenuTimeoutRef.current = null
    }
  }

  const handleUserMenuLeave = () => {
    if (userMenuTimeoutRef.current) {
      clearTimeout(userMenuTimeoutRef.current)
    }
    userMenuTimeoutRef.current = setTimeout(() => {
      setUserMenuOpen(false)
    }, 150)
  }

  function handleLogout() {
    logout()
    setUserMenuOpen(false)
    navigate('/')
  }

  function handleSearch(e) {
    const query = e.target.value
    setSearchQuery(query)
    if (query.trim()) {
      navigate(`/shop?search=${encodeURIComponent(query.trim())}`)
    } else if (window.location.pathname === '/shop') {
      navigate(`/shop`)
    }
  }

  function handlePillClick(term) {
    setSearchQuery(term)
    setIsSearchFocused(false)
    navigate(`/shop?search=${encodeURIComponent(term)}`)
  }

  function handleAIChat() {
    document.dispatchEvent(new CustomEvent('open-ai-chat'))
  }

  const toggleCategory = (label) => {
    setOpenCategory((prev) => (prev === label ? null : label))
  }

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.4 }}
        style={{
          boxShadow: scrolled ? '0 4px 20px rgba(0,0,0,0.06)' : '0 1px 4px rgba(0,0,0,0.03)',
          top: -topbarShift,
        }}
        className="fixed top-0 left-0 right-0 z-[100] transition-all duration-300 flex flex-col bg-transparent font-montserrat"
      >
        {/* Tier 1: Announcement Bar */}
        <div ref={topbarRef} className="relative z-50">
          <Topbar />
        </div>

        {/* Tier 2: Main Brand Row */}
        <div
          ref={mainRowRef}
          className="w-full bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-[0_1px_3px_rgba(0,0,0,0.02)] relative z-50"
        >
          <div className="max-w-7xl mx-auto px-3.5 sm:px-6 lg:px-8 w-full">
            <div className="flex items-center justify-between h-14 gap-4 md:gap-8">
              
              {/* Brand Logo (Crisp, balanced optical luxury sizing) */}
              <Link to="/" className="flex items-center shrink-0 gap-2 sm:gap-2.5 focus:outline-none" aria-label="Ray Optics Home">
                <img
                  src={site.branding.logo_mark}
                  alt=""
                  className="h-[22px] sm:h-[24px] md:h-[25px] w-auto object-contain shrink-0"
                />
                <div className="h-[16px] sm:h-[18px] md:h-[18px] w-px bg-gray-200/90 shrink-0" />
                <img
                  src={site.branding.logo_text}
                  alt={site.settings.store_name || 'Rayoptics'}
                  className="h-[26px] sm:h-[28px] md:h-[30px] w-auto object-contain shrink-0"
                />
              </Link>

              {/* Search Bar (Desktop) */}
              <div className="hidden md:flex flex-1 max-w-3xl relative mx-6">
                <Search className="absolute left-5 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" strokeWidth={1.75} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearch}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                  placeholder={site.header.search_placeholder}
                  className="w-full pl-12 pr-6 py-2 bg-gray-50/70 hover:bg-gray-50 focus:bg-white border border-gray-200 rounded-full text-[15px] focus:outline-none focus:border-[#3A8A22] focus:ring-2 focus:ring-[#5FC03F]/20 transition-all placeholder-gray-400"
                />

                {/* Trending Searches Popup (Desktop) */}
                <AnimatePresence>
                  {isSearchFocused && (
                    <motion.div
                      initial={{ opacity: 0, y: -8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -8 }}
                      className="absolute top-[calc(100%+8px)] left-0 right-0 bg-white border border-gray-100 rounded-2xl shadow-2xl shadow-black/10 p-4 z-50"
                    >
                      <p className="text-xs font-semibold text-gray-500 mb-3 uppercase tracking-wider">Trending Searches</p>
                      <div className="flex flex-wrap gap-2">
                        {TRENDING_SEARCHES.map((term) => (
                          <button
                            key={term}
                            onClick={() => handlePillClick(term)}
                            className="px-3 py-1.5 bg-gray-50 hover:bg-[#5FC03F]/10 text-gray-700 hover:text-[#3A8A22] text-sm rounded-full transition-colors border border-gray-200 hover:border-[#5FC03F]/50"
                          >
                            {term}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Right Action Cluster */}
              <div className="flex items-center gap-1.5 sm:gap-3.5">
                
                {/* Desktop: User Menu / Sign In */}
                {isAuthenticated ? (
                  <div
                    ref={userMenuRef}
                    className="relative hidden md:block"
                    onMouseEnter={handleUserMenuEnter}
                    onMouseLeave={handleUserMenuLeave}
                  >
                    <button
                      type="button"
                      onClick={() => {
                        handleUserMenuEnter()
                        setUserMenuOpen((prev) => !prev)
                      }}
                      className="flex items-center gap-1.5 p-1.5 rounded-lg hover:text-[#3A8A22] transition-colors"
                      aria-expanded={userMenuOpen}
                      aria-haspopup="menu"
                    >
                      <span className="text-[14px] font-semibold text-[#111111] hover:text-[#3A8A22] transition-colors">
                        Hi, {user?.full_name?.split(' ')[0] || 'User'}
                      </span>
                      <ChevronDown strokeWidth={ACTION_STROKE} className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${userMenuOpen ? 'rotate-180' : ''}`} />
                    </button>

                    <AnimatePresence>
                      {userMenuOpen && (
                        <div className="absolute right-0 top-full pt-1.5 z-50">
                          <motion.div
                            initial={{ opacity: 0, y: 6, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 6, scale: 0.95 }}
                            transition={{ duration: 0.15 }}
                            className="w-56 bg-white border border-gray-100 rounded-xl shadow-xl overflow-hidden"
                          >
                            <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/70">
                              <p className="text-[#111111] text-sm font-semibold">{user?.full_name}</p>
                              <p className="text-gray-500 text-xs truncate">{user?.email}</p>
                            </div>
                            <div className="py-1">
                              {[
                                { label: 'My Orders',        href: '/account/orders',        icon: Package },
                                { label: 'Addresses',        href: '/account/addresses',     icon: MapPin },
                                { label: 'Prescriptions',    href: '/account/prescriptions', icon: FileText },
                                { label: 'Find Store',       href: '/stores',                icon: Store },
                                { label: 'Account Settings', href: '/account',               icon: Settings },
                                ...(user?.role === 'super_admin' ? [{ label: 'Admin Panel', href: '/admin', icon: Settings }] : []),
                              ].map((item) => (
                                <SmartLink
                                  key={item.label}
                                  to={item.href}
                                  onClick={() => setUserMenuOpen(false)}
                                  className="flex items-center gap-3 px-4 py-2 text-sm text-[#111111] hover:text-[#3A8A22] hover:bg-gray-50 transition-colors"
                                >
                                  <item.icon className="h-4 w-4" />
                                  {item.label}
                                </SmartLink>
                              ))}
                              <button
                                onClick={handleLogout}
                                className="flex items-center gap-3 w-full px-4 py-2 text-sm text-red-500 hover:bg-red-50 transition-colors"
                              >
                                <LogOut className="h-4 w-4" />
                                Logout
                              </button>
                            </div>
                          </motion.div>
                        </div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  <div className="relative hidden md:block group">
                    <Link
                      to="/login"
                      className="block p-1.5 text-[#111111] group-hover:text-[#3A8A22] focus-visible:text-[#3A8A22] transition-colors"
                      aria-label="Sign In"
                    >
                      <User className={ACTION_ICON} strokeWidth={ACTION_STROKE} />
                    </Link>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <div className="w-56 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden py-1">
                        {[
                          { label: 'Sign In / Sign Up', href: '/login',          icon: User },
                          { label: 'Track Order',       href: '/account/orders', icon: Package },
                          { label: 'Find Store',        href: '/stores',         icon: Store },
                        ].map((item) => (
                          <SmartLink
                            key={item.label}
                            to={item.href}
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-[#111111] hover:bg-gray-50 hover:text-[#3A8A22] transition-colors"
                          >
                            <item.icon className="h-4 w-4" strokeWidth={ACTION_STROKE} />
                            {item.label}
                          </SmartLink>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Desktop: Help */}
                <Link
                  to="/help"
                  className="hidden md:flex items-center p-1.5 text-[#111111] hover:text-[#3A8A22] transition-colors"
                  aria-label="Customer Help"
                >
                  <HelpCircle className={ACTION_ICON} strokeWidth={ACTION_STROKE} />
                </Link>

                {/* Desktop: Wishlist */}
                <Link
                  to="/wishlist"
                  className="hidden md:flex relative items-center p-1.5 text-[#111111] hover:text-[#3A8A22] transition-colors"
                  aria-label="Wishlist"
                >
                  <Heart className={ACTION_ICON} strokeWidth={ACTION_STROKE} />
                  {wishlistItems.length > 0 && (
                    <span className="absolute -top-1.5 -right-2 bg-[#D14A2E] text-white text-[10px] leading-none rounded-full h-[20px] w-[20px] flex items-center justify-center font-bold ring-2 ring-white shadow-xs">
                      {wishlistItems.length > 9 ? '9+' : wishlistItems.length}
                    </span>
                  )}
                </Link>

                {/* Cart (Both Mobile & Desktop) */}
                <Link
                  to="/cart"
                  className="relative p-2 rounded-full text-[#111111] hover:text-[#3A8A22] hover:bg-gray-100 active:scale-95 transition-all"
                  aria-label={`Cart with ${cartCount} items`}
                >
                  <ShoppingCart className="h-5 w-5" strokeWidth={ACTION_STROKE} />
                  {cartCount > 0 && (
                    <motion.span
                      key={cartCount}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute top-0.5 right-0.5 bg-[#D14A2E] text-white text-[10px] leading-none rounded-full h-[18px] w-[18px] flex items-center justify-center font-bold ring-2 ring-white shadow-xs"
                    >
                      {cartCount > 9 ? '9+' : cartCount}
                    </motion.span>
                  )}
                </Link>

                {/* Mobile Hamburger Menu Toggle */}
                <button
                  type="button"
                  onClick={() => setMobileOpen(true)}
                  aria-label="Open navigation menu"
                  aria-expanded={mobileOpen}
                  className="md:hidden p-2 rounded-full text-[#111111] hover:text-[#3A8A22] hover:bg-gray-100 active:scale-95 transition-all focus:outline-none"
                >
                  <Menu className="h-5 w-5" strokeWidth={ACTION_STROKE} />
                </button>
              </div>
            </div>

            {/* Mobile Search Bar Row (App-like pill) */}
            <div className="md:hidden px-1 pb-2.5 pt-0.5">
              <div className="relative">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearch}
                  onFocus={() => setIsSearchFocused(true)}
                  onBlur={() => setTimeout(() => setIsSearchFocused(false), 200)}
                  placeholder="Search frames, sunglasses, lenses..."
                  className="w-full pl-9 pr-8 py-2 bg-gray-100/80 hover:bg-gray-100 focus:bg-white border border-transparent focus:border-[#3A8A22] rounded-full text-[13px] font-medium text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#5FC03F]/20 transition-all shadow-xs"
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => { setSearchQuery(''); navigate('/shop'); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 rounded-full"
                    aria-label="Clear search"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}

                {/* Mobile Trending Searches Popup */}
                <AnimatePresence>
                  {isSearchFocused && (
                    <motion.div
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      className="absolute top-[calc(100%+8px)] left-0 right-0 bg-white border border-gray-100 rounded-2xl shadow-xl p-3.5 z-50"
                    >
                      <p className="text-[11px] font-bold text-gray-400 mb-2.5 uppercase tracking-wider">Trending Searches</p>
                      <div className="flex flex-wrap gap-1.5">
                        {TRENDING_SEARCHES.map((term) => (
                          <button
                            key={term}
                            onClick={() => handlePillClick(term)}
                            className="px-2.5 py-1 bg-gray-50 hover:bg-[#5FC03F]/10 text-gray-700 hover:text-[#3A8A22] text-[12px] font-medium rounded-full transition-colors border border-gray-200 hover:border-[#5FC03F]/50"
                          >
                            {term}
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>

        {/* Search Overlay */}
        <AnimatePresence>
          {isSearchFocused && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/30 backdrop-blur-xs z-40 pointer-events-none"
            />
          )}
        </AnimatePresence>

        {/* Tier 3: Desktop Category Navigation Bar */}
        <div ref={menuRowRef} className="hidden md:block w-full bg-white border-b border-gray-100 relative z-30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
            <div className="flex items-center justify-center gap-4 lg:gap-8 xl:gap-10 h-10">
              {NAV_LINKS.map((link) => (
                link.dropdown ? (
                  <div key={link.label} className="relative group">
                    <button className="flex items-center gap-1 text-[13px] lg:text-[14px] font-semibold text-[#111] hover:text-[#3A8A22] transition-colors">
                      {link.label}
                      <ChevronDown className="h-3.5 w-3.5 opacity-70 group-hover:rotate-180 transition-transform" />
                    </button>
                    <div className="absolute top-full left-0 pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                      <div className="w-52 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden py-1">
                        {link.dropdown.map((item) => (
                          <SmartLink
                            key={item.label}
                            to={item.href}
                            className="block px-4 py-2.5 text-sm text-[#111111] hover:bg-gray-50 hover:text-[#3A8A22] transition-colors"
                          >
                            {item.label}
                          </SmartLink>
                        ))}
                      </div>
                    </div>
                  </div>
                ) : (
                  <SmartLink
                    key={link.label}
                    to={link.href}
                    className="relative text-[13px] lg:text-[14px] font-semibold text-[#111] hover:text-[#3A8A22] transition-colors"
                  >
                    {link.label}
                  </SmartLink>
                )
              ))}
              <Link
                to="/request-specs"
                className="inline-flex items-center gap-1.5 text-[12px] lg:text-[13px] font-bold text-[#3A8A22] hover:text-[#2d6e1b] transition-all bg-[#5FC03F]/10 hover:bg-[#5FC03F]/20 px-3 py-1 rounded-full border border-[#5FC03F]/30 shadow-xs ml-2"
              >
                <Sparkles className="h-3.5 w-3.5 text-[#3A8A22]" />
                Request Specific Specs
              </Link>
            </div>
          </div>
        </div>

        {/* ── World-Class Mobile Side Drawer Navigation ── */}
        <AnimatePresence>
          {mobileOpen && (
            <>
              {/* Frosted Backdrop */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={() => setMobileOpen(false)}
                className="fixed inset-0 bg-black/60 backdrop-blur-xs z-[200] md:hidden"
                aria-hidden="true"
              />

              {/* Side Drawer Sheet */}
              <motion.aside
                initial={{ x: '-100%' }}
                animate={{ x: 0 }}
                exit={{ x: '-100%' }}
                transition={{ type: 'spring', damping: 28, stiffness: 300 }}
                className="fixed inset-y-0 left-0 w-[84vw] max-w-[340px] bg-white z-[201] md:hidden shadow-2xl flex flex-col font-montserrat"
                aria-label="Mobile Navigation Menu"
              >
                {/* Drawer Top Bar */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-white">
                  <Link to="/" onClick={() => setMobileOpen(false)} className="flex items-center gap-2">
                    <img
                      src={site.branding.logo_mark}
                      alt=""
                      className="h-[22px] w-auto object-contain shrink-0"
                    />
                    <div className="h-[16px] w-px bg-gray-200 shrink-0" />
                    <img
                      src={site.branding.logo_text}
                      alt={site.settings.store_name || 'Rayoptics'}
                      className="h-[26px] w-auto object-contain shrink-0"
                    />
                  </Link>
                  <button
                    onClick={() => setMobileOpen(false)}
                    aria-label="Close menu"
                    className="p-1.5 rounded-full text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* User Status Strip */}
                <div className="p-4 bg-gradient-to-r from-gray-50 via-[#F5F8F5] to-gray-50 border-b border-gray-100">
                  {isAuthenticated ? (
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-[#5FC03F] text-black font-extrabold flex items-center justify-center text-sm shadow-sm">
                          {user?.full_name?.[0] || 'U'}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900 leading-tight">{user?.full_name}</p>
                          <p className="text-xs text-gray-500 truncate max-w-[150px]">{user?.email}</p>
                        </div>
                      </div>
                      <Link
                        to="/account"
                        onClick={() => setMobileOpen(false)}
                        className="text-xs font-bold text-[#3A8A22] hover:underline"
                      >
                        Account ›
                      </Link>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-bold text-gray-900">Welcome, Guest</p>
                        <p className="text-xs text-gray-500">Sign in for orders & wishlists</p>
                      </div>
                      <Link
                        to="/login"
                        onClick={() => setMobileOpen(false)}
                        className="px-3.5 py-1.5 bg-[#3A8A22] hover:bg-[#2d6e1b] text-white text-xs font-bold rounded-lg shadow-sm"
                      >
                        Sign In
                      </Link>
                    </div>
                  )}
                </div>

                {/* Scrollable Nav Items */}
                <div className="flex-1 overflow-y-auto px-4 py-3 space-y-1">
                  <div className="px-3 py-1.5 text-[11px] font-bold text-gray-400 uppercase tracking-wider">
                    Eyewear Categories
                  </div>

                  {NAV_LINKS.map((link) => {
                    const hasSub = !!link.dropdown?.length
                    const isExpanded = openCategory === link.label

                    if (hasSub) {
                      return (
                        <div key={link.label} className="border-b border-gray-100/60 pb-1">
                          <button
                            type="button"
                            onClick={() => toggleCategory(link.label)}
                            className="w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-semibold text-gray-800 hover:bg-gray-50 transition-colors"
                          >
                            <span>{link.label}</span>
                            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180 text-[#3A8A22]' : ''}`} />
                          </button>
                          {isExpanded && (
                            <div className="pl-4 pr-2 py-1 space-y-1 animate-fade-in">
                              {link.dropdown.map((sub) => (
                                <SmartLink
                                  key={sub.label}
                                  to={sub.href}
                                  onClick={() => setMobileOpen(false)}
                                  className="block px-3 py-2 text-xs font-medium text-gray-600 hover:text-[#3A8A22] hover:bg-emerald-50/50 rounded-lg transition-colors"
                                >
                                  {sub.label}
                                </SmartLink>
                              ))}
                            </div>
                          )}
                        </div>
                      )
                    }

                    return (
                      <SmartLink
                        key={link.label}
                        to={link.href}
                        onClick={() => setMobileOpen(false)}
                        className="flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-semibold text-gray-800 hover:bg-gray-50 hover:text-[#3A8A22] transition-colors"
                      >
                        <span>{link.label}</span>
                        <ChevronRight className="w-4 h-4 text-gray-300" />
                      </SmartLink>
                    )
                  })}

                  {/* Highlights / Special CTAs */}
                  <div className="pt-3 border-t border-gray-100 mt-3 space-y-1">
                    <Link
                      to="/request-specs"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl bg-[#5FC03F]/10 text-[#3A8A22] font-bold text-xs uppercase tracking-wide border border-[#5FC03F]/30"
                    >
                      <Sparkles className="w-4 h-4 text-[#3A8A22]" />
                      <span>Request Specific Specs</span>
                    </Link>

                    <Link
                      to="/account/orders"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-700 hover:bg-gray-50 text-sm font-medium"
                    >
                      <Package className="w-4 h-4 text-gray-400" />
                      <span>Track Order</span>
                    </Link>

                    <Link
                      to="/stores"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-700 hover:bg-gray-50 text-sm font-medium"
                    >
                      <Store className="w-4 h-4 text-gray-400" />
                      <span>Find a Store</span>
                    </Link>

                    <Link
                      to="/help"
                      onClick={() => setMobileOpen(false)}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-gray-700 hover:bg-gray-50 text-sm font-medium"
                    >
                      <HelpCircle className="w-4 h-4 text-gray-400" />
                      <span>Help & FAQs</span>
                    </Link>
                  </div>
                </div>

                {/* Drawer Footer */}
                <div className="p-4 border-t border-gray-100 bg-gray-50/60 flex items-center justify-between text-xs text-gray-500">
                  <a href="tel:919490424422" className="flex items-center gap-1.5 hover:text-[#3A8A22] font-medium">
                    <PhoneCall className="w-3.5 h-3.5 text-[#3A8A22]" />
                    <span>Call Support</span>
                  </a>
                  {isAuthenticated && (
                    <button
                      onClick={() => { handleLogout(); setMobileOpen(false); }}
                      className="text-red-500 hover:text-red-700 font-semibold flex items-center gap-1"
                    >
                      <LogOut className="w-3.5 h-3.5" />
                      <span>Logout</span>
                    </button>
                  )}
                </div>
              </motion.aside>
            </>
          )}
        </AnimatePresence>
      </motion.nav>
    </>
  )
}
